import { z } from 'zod';
export const RoleCodes = [
    'RECEPCION',
    'TECNICA',
    'MEDICO',
    'FACTURACION',
    'COORDINACION',
    'ADMINISTRADOR',
    'AUDITOR',
    'SUPERVISOR',
    'INVESTIGACION',
    'SISTEMA',
];
export const ProfessionalTypes = [
    'RECEPCION',
    'TECNICA',
    'MEDICO',
    'FACTURACION',
    'COORDINACION',
    'ADMINISTRADOR',
];
export const PatientStatuses = [
    'ACTIVO',
    'INACTIVO',
    'FALLECIDO',
    'DUPLICADO_CONSOLIDADO',
];
export const PatientDataStatuses = [
    'COMPLETO',
    'PENDIENTE_VALIDACION',
    'INCOMPLETO',
];
export const AttentionOrigins = ['CONSULTORIO', 'CLINICA_EXTERNA'];
export const ProcedureStatuses = [
    'PENDIENTE',
    'EN_PROCESO',
    'REALIZADO',
    'INCOMPLETO',
    'NO_REALIZADO',
];
export const ReportStatuses = [
    'BORRADOR',
    'PENDIENTE_APROBACION',
    'DEVUELTO',
    'APROBADO',
    'REEMPLAZADO',
];
export const GenerationStatuses = [
    'NO_INICIADO',
    'PENDIENTE',
    'GENERANDO',
    'GENERADO',
    'ERROR',
];
export const DeliveryStatuses = [
    'NO_INICIADO',
    'PENDIENTE',
    'ENVIADO',
    'ERROR',
];
export const DocumentTypes = [
    'CAPTURA',
    'ESTUDIO_FUENTE',
    'VIDEO',
    'SOPORTE',
    'WORD_TEMPORAL',
    'INFORME_FINAL',
    'PLANTILLA',
    'FIRMA',
    'OTRO',
];
export const DocumentStatuses = [
    'CARGADO',
    'VALIDADO',
    'GENERADO',
    'ENVIADO',
    'REEMPLAZADO',
    'ERROR',
];
export const DocumentSources = [
    'APLICACION',
    'PROFUSION',
    'GENERADO',
    'IMPORTADO',
    'MICROSOFT_GRAPH',
];
export const ConnectivityTypes = [
    'NO_APLICA',
    'WIFI_CLINICA',
    'ETHERNET',
    'HOTSPOT_PERSONAL',
    'SIM_CORPORATIVA',
    'MIFI',
    'SIN_CONECTIVIDAD',
    'OTRO',
];
export const LiveSessionStatuses = [
    'PLANIFICADA',
    'INICIADA',
    'FINALIZADA',
    'CANCELADA',
    'FALLIDA',
];
export const BillingStatuses = [
    'NO_REQUERIDA',
    'PENDIENTE_ENVIO',
    'ENVIADA',
    'RECIBIDA',
    'PENDIENTE_INFORMACION',
    'FACTURADA',
    'NO_FACTURABLE',
    'DEVUELTA',
];
export const ExternalSystemTypes = ['SALUDTOOLS', 'PROFUSION', 'OTRO'];
export const ExternalHandoffStatuses = [
    'PENDIENTE',
    'REGISTRADO',
    'VERIFICADO',
    'NO_APLICA',
    'ERROR',
];
export const DynamicFieldTypes = [
    'TEXT',
    'TEXTAREA',
    'NUMBER',
    'BOOLEAN',
    'DATE',
    'DATETIME',
    'SELECT',
    'MULTISELECT',
];
export const AutomationKinds = [
    'STUDY_STORAGE_INITIALIZE',
    'REPORT_GENERATE_DOCUMENT',
    'DOCUMENT_SEND',
    'PATIENT_SYNC_ACTIVE_STUDIES',
    'SLA_SCAN',
    'SLA_NOTIFICATION_SEND',
    'MEDICAL_REVIEW_NOTIFICATION_SEND',
];
export const AutomationStatuses = [
    'PENDIENTE',
    'EN_PROCESO',
    'COMPLETADO',
    'ERROR',
    'CANCELADO',
];
export const ProcessStateCodes = [
    'REG_INICIADO',
    'PEND_INFO',
    'INFO_COMPLETA',
    'EST_EN_PROCESO',
    'EST_REALIZADO',
    'PEND_REV_MEDICA',
    'INF_EN_ELAB',
    'PEND_APROBACION',
    'DEV_CORRECCION',
    'APROBADO',
    'GENERANDO_DOC',
    'DOC_GENERADO',
    'PEND_ENVIO',
    'ENVIADO',
    'PEND_FACTURACION',
    'FACTURADO',
    'CERRADO',
    'ERROR_AUT',
    'ANULADO',
];
export const TransitionActionCodes = [
    'GUARDAR_BASICOS',
    'COMPLETAR_INFO',
    'INICIAR_ESTUDIO',
    'FINALIZAR_ESTUDIO',
    'ENVIAR_REVISION',
    'INICIAR_INFORME',
    'ENVIAR_APROBACION',
    'APROBAR',
    'DEVOLVER',
    'DEVOLVER_TECNICA',
    'REENVIAR',
    'GENERAR_DOCUMENTO',
    'DOCUMENTO_OK',
    'PREPARAR_ENVIO',
    'ENVIAR',
    'ENVIAR_FACTURACION',
    'MARCAR_FACTURADO',
    'CERRAR',
    'ANULAR',
    'MARCAR_ERROR',
    'REINTENTAR',
];
const optionalTrimmedString = (max = 500) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((value) => (value === '' ? null : value));
const optionalEmail = z
    .union([z.string().trim().email(), z.literal(''), z.null()])
    .optional()
    .transform((value) => (value ? value.toLowerCase() : null));
const nullableDate = z.coerce.date().optional().nullable();
export const PaginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const PatientInputSchema = z
    .object({
    identificationType: z.string().trim().min(1).max(30),
    identificationNumber: z.string().trim().min(1).max(60),
    firstName: z.string().trim().min(1).max(120),
    secondName: optionalTrimmedString(120),
    firstLastName: z.string().trim().min(1).max(120),
    secondLastName: optionalTrimmedString(120),
    birthDate: nullableDate,
    sex: optionalTrimmedString(60),
    primaryPhone: optionalTrimmedString(40),
    email: optionalEmail,
    address: optionalTrimmedString(500),
    city: optionalTrimmedString(120),
    department: optionalTrimmedString(120),
    status: z.enum(PatientStatuses).default('ACTIVO'),
    dataStatus: z.enum(PatientDataStatuses).default('COMPLETO'),
    dataValidationNotes: optionalTrimmedString(4000),
    createdFromOrigin: z.enum(AttentionOrigins).default('CONSULTORIO'),
    authorizesData: z.boolean().default(false),
    authorizationDate: nullableDate,
    dataObservation: optionalTrimmedString(2000),
    rowVersion: z.number().int().positive().optional(),
})
    .superRefine((value, ctx) => {
    if (value.birthDate && value.birthDate > new Date()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['birthDate'],
            message: 'La fecha no puede ser futura.',
        });
    }
    if (value.dataStatus === 'COMPLETO') {
        const required = [
            [
                'birthDate',
                'La fecha de nacimiento es obligatoria para una ficha completa.',
            ],
            ['primaryPhone', 'El teléfono es obligatorio para una ficha completa.'],
            ['address', 'La dirección es obligatoria para una ficha completa.'],
            ['city', 'La ciudad es obligatoria para una ficha completa.'],
        ];
        for (const [key, message] of required) {
            if (!value[key])
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message });
        }
    }
});
export const PatientValidationSchema = z.object({
    dataStatus: z.enum(PatientDataStatuses),
    notes: optionalTrimmedString(4000),
    rowVersion: z.number().int().positive(),
});
export const PatientSearchSchema = PaginationSchema.extend({
    q: z.string().trim().max(120).optional().default(''),
    status: z.enum(PatientStatuses).optional(),
    dataStatus: z.enum(PatientDataStatuses).optional(),
});
const OperationalDateRangeBaseSchema = z.object({
    studyDateFrom: z.coerce.date().optional(),
    studyDateTo: z.coerce.date().optional(),
});
function validateOperationalDateRange(value, ctx) {
    if (value.studyDateFrom && value.studyDateTo && value.studyDateFrom > value.studyDateTo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['studyDateTo'],
            message: 'La fecha final no puede ser anterior a la fecha inicial.',
        });
    }
}
export const OperationalDateRangeSchema = OperationalDateRangeBaseSchema.superRefine(validateOperationalDateRange);
export const PatientQuickSearchSchema = z.object({
    q: z.string().trim().min(2).max(120),
    limit: z.coerce.number().int().min(1).max(20).default(10),
});
export const Cie10SearchSchema = z.object({
    q: z.string().trim().min(2).max(120),
    limit: z.coerce.number().int().min(1).max(20).default(12),
});
export const StudyInputSchema = z
    .object({
    patientId: z.coerce.number().int().positive(),
    origin: z.enum(AttentionOrigins),
    institutionId: z.coerce.number().int().positive().optional().nullable(),
    serviceLocationId: z.coerce.number().int().positive().optional().nullable(),
    serviceLocation: optionalTrimmedString(200),
    studyDate: z.coerce.date(),
    scheduledAt: nullableDate,
    studyTypeId: z.coerce.number().int().positive(),
    reason: z.string().trim().min(1).max(4000),
    referringProfessional: optionalTrimmedString(200),
    referringInstitution: optionalTrimmedString(200),
    profusionPatientCode: optionalTrimmedString(120),
    profusionStudyCode: optionalTrimmedString(120),
    doctorId: z.coerce.number().int().positive(),
    technicianEmail: optionalEmail,
    receptionEmail: optionalEmail,
    administrativeCondition: optionalTrimmedString(120),
    payerName: optionalTrimmedString(200),
    agreementId: z.coerce.number().int().positive().optional().nullable(),
    authorizationNumber: optionalTrimmedString(120),
    administrativeObservation: optionalTrimmedString(4000),
    connectivityType: z.enum(ConnectivityTypes).default('NO_APLICA'),
    requiresLiveSupport: z.boolean().default(false),
    sendToPatient: z.boolean().default(true),
    sendToAilyn: z.boolean().default(true),
    additionalRecipients: z
        .array(z
        .string()
        .trim()
        .email()
        .transform((v) => v.toLowerCase()))
        .max(10)
        .default([]),
    dynamicValues: z.record(z.string(), z.unknown()).default({}),
    rowVersion: z.number().int().positive().optional(),
})
    .superRefine((value, ctx) => {
    if (value.origin === 'CLINICA_EXTERNA' &&
        !value.institutionId &&
        !value.serviceLocation?.trim() &&
        !value.agreementId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['serviceLocation'],
            message: 'Registre la sede o lugar externo, o seleccione un convenio activo para clínica externa.',
        });
    }
    if (value.serviceLocationId && !value.institutionId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['serviceLocationId'],
            message: 'La sede catalogada requiere una institución física.',
        });
    }
    if (value.origin === 'CLINICA_EXTERNA' && !value.technicianEmail) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['technicianEmail'],
            message: 'La técnica responsable es obligatoria para clínica externa.',
        });
    }
    if (value.origin === 'CONSULTORIO' && !value.receptionEmail && !value.technicianEmail) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['receptionEmail'],
            message: 'Seleccione recepción o técnica responsable para consultorio.',
        });
    }
});
export const DashboardQuerySchema = OperationalDateRangeSchema;
export const StudySearchSchema = PaginationSchema.merge(OperationalDateRangeBaseSchema)
    .extend({
    q: z.string().trim().max(150).optional().default(''),
    stateCode: z.string().trim().max(60).optional(),
    responsibleRole: z.string().trim().max(60).optional(),
    responsibleEmail: z.string().trim().max(320).optional(),
    origin: z.enum(AttentionOrigins).optional(),
    patientId: z.coerce.number().int().positive().optional(),
    billingStatus: z.enum(BillingStatuses).optional(),
    dataStatus: z.enum(PatientDataStatuses).optional(),
})
    .superRefine(validateOperationalDateRange);
export const ReceptionInputSchema = z.object({
    administrativeCondition: optionalTrimmedString(120),
    payerName: optionalTrimmedString(200),
    agreementId: z.coerce.number().int().positive().optional().nullable(),
    authorizationNumber: optionalTrimmedString(120),
    administrativeObservation: optionalTrimmedString(4000),
    scheduledAt: nullableDate,
    doctorId: z.coerce.number().int().positive().optional(),
    receptionEmail: optionalEmail,
    rowVersion: z.number().int().positive(),
});
export const DeliverySettingsInputSchema = z
    .object({
    sendToPatient: z.boolean(),
    sendToAilyn: z.boolean(),
    additionalRecipients: z.array(z.string().email()).max(10).default([]),
    rowVersion: z.number().int().positive(),
})
    .refine((value) => value.sendToPatient ||
    value.sendToAilyn ||
    value.additionalRecipients.length > 0, {
    message: 'Debe seleccionar al menos un destinatario.',
});
export const TechnicalAdditionalFieldSchema = z.object({
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(8000),
});
export const TechnicalCaptureInputSchema = z.object({
    weightKg: z.coerce.number().positive().max(500).optional().nullable(),
    heightCm: z.coerce.number().positive().max(300).optional().nullable(),
    systolicBloodPressure: z.coerce
        .number()
        .positive()
        .max(350)
        .optional()
        .nullable(),
    diastolicBloodPressure: z.coerce
        .number()
        .positive()
        .max(250)
        .optional()
        .nullable(),
    heartRateBpm: z.coerce.number().positive().max(300).optional().nullable(),
    respiratoryRateBpm: z.coerce
        .number()
        .positive()
        .max(120)
        .optional()
        .nullable(),
    temperatureC: z.coerce.number().min(25).max(45).optional().nullable(),
    patientCondition: optionalTrimmedString(4000),
    relevantHistory: optionalTrimmedString(4000),
    technicalObservations: z.string().trim().min(1).max(8000),
    procedureIncidents: optionalTrimmedString(8000),
    studyStarted: z.boolean().default(false),
    studyCompleted: z.boolean().default(false),
    sourceFilesComplete: z.boolean().default(false),
    technicalConfirmation: z.boolean().default(false),
    deliveryComment: optionalTrimmedString(4000),
    procedureStatus: z.enum(ProcedureStatuses),
    nonPerformanceReason: optionalTrimmedString(4000),
    startedAt: nullableDate,
    endedAt: nullableDate,
    connectivityType: z.enum(ConnectivityTypes).default('NO_APLICA'),
    profusionPatientCode: optionalTrimmedString(120),
    profusionStudyCode: optionalTrimmedString(120),
    dynamicValues: z.record(z.string(), z.unknown()).default({}),
    additionalFields: z
        .array(TechnicalAdditionalFieldSchema)
        .max(20)
        .default([])
        .superRefine((fields, context) => {
        const titles = new Set();
        fields.forEach((field, index) => {
            const normalizedTitle = field.title.toLocaleLowerCase('es');
            if (titles.has(normalizedTitle)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'title'],
                    message: 'El título no puede repetirse dentro de la captura.',
                });
            }
            titles.add(normalizedTitle);
        });
    }),
});
export const LiveSessionInputSchema = z.object({
    status: z.enum(LiveSessionStatuses),
    startedAt: nullableDate,
    endedAt: nullableDate,
    technicalUserEmail: z
        .string()
        .trim()
        .email()
        .transform((v) => v.toLowerCase()),
    doctorUserEmail: z
        .string()
        .trim()
        .email()
        .transform((v) => v.toLowerCase()),
    connectivityType: z.enum(ConnectivityTypes),
    meetingReference: optionalTrimmedString(2000),
    incidentDescription: optionalTrimmedString(8000),
    result: optionalTrimmedString(8000),
    recordingUrl: optionalTrimmedString(2000),
    recordingAuthorized: z.boolean().default(false),
});
export const ReportDiagnosisInputSchema = z.object({
    isPrimary: z.boolean().default(false),
    cie10Code: z.string().trim().min(1).max(20),
    description: z.string().trim().min(1).max(1000),
    diagnosisType: optionalTrimmedString(120),
    classification: optionalTrimmedString(120),
    registeredAt: nullableDate,
});
export const RichReportContentSchema = z.object({
    type: z.literal('doc').default('doc'),
    content: z.array(z.unknown()).default([]),
});
export const ReportInputSchema = z.object({
    textReport: z.string().trim().min(1).max(100000),
    findings: optionalTrimmedString(100000),
    conclusionDiagnosis: z.string().trim().min(1).max(100000),
    recommendations: optionalTrimmedString(100000),
    chiefComplaint: optionalTrimmedString(100000),
    currentIllness: optionalTrimmedString(100000),
    physicalExam: optionalTrimmedString(100000),
    clinicalImpression: optionalTrimmedString(100000),
    managementPlan: optionalTrimmedString(100000),
    careModality: optionalTrimmedString(200),
    diagnoses: z.array(ReportDiagnosisInputSchema).max(20).optional(),
    internalObservations: optionalTrimmedString(10000),
    medicalDecision: optionalTrimmedString(10000),
    richContentJson: RichReportContentSchema.optional().nullable(),
    richContentText: optionalTrimmedString(200000),
    selectedTemplateId: z.coerce.number().int().positive().optional().nullable(),
    professionalId: z.coerce.number().int().positive(),
});
export const ClinicalReportTemplateInputSchema = z.object({
    id: z.coerce.number().int().positive().optional().nullable(),
    name: z.string().trim().min(1).max(180),
    code: optionalTrimmedString(120),
    description: optionalTrimmedString(1000),
    category: optionalTrimmedString(120),
    studyTypeId: z.coerce.number().int().positive().optional().nullable(),
    sourceStudyId: z.coerce.number().int().positive().optional().nullable(),
    contentJson: RichReportContentSchema,
    contentText: z.string().trim().min(1).max(200000),
    contentKind: z.enum(['PLANTILLA_COMPLETA', 'BLOQUE_CLINICO']).default('PLANTILLA_COMPLETA'),
    status: z.enum(['BORRADOR', 'PUBLICADA', 'INACTIVA']).optional(),
    createNewVersion: z.boolean().default(false),
    active: z.boolean().default(true),
    systemTemplate: z.boolean().default(false),
    displayOrder: z.coerce.number().int().min(0).default(100),
});
const queryBoolean = z.preprocess((value) => {
    if (value === undefined || value === null || value === '')
        return false;
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'string')
        return value.toLowerCase() === 'true';
    return value;
}, z.boolean());
export const ClinicalReportTemplateQuerySchema = z.object({
    studyTypeId: z.coerce.number().int().positive().optional(),
    includeInactive: queryBoolean.default(false),
});
export const ClinicalTemplatePreferenceInputSchema = z.object({
    studyTypeId: z.coerce.number().int().positive(),
    templateId: z.coerce.number().int().positive(),
});
export const ClinicalTemplatePreferenceQuerySchema = z.object({
    studyTypeId: z.coerce.number().int().positive(),
});
export const ReportReturnSchema = z.object({
    comment: z.string().trim().min(5).max(4000),
});
export const TransitionRequestSchema = z.object({
    actionCode: z.enum(TransitionActionCodes),
    comment: optionalTrimmedString(4000),
    expectedStateCode: z.string().trim().min(1).max(60),
    expectedRowVersion: z.number().int().positive(),
});
export const DocumentMetadataInputSchema = z.object({
    studyId: z.coerce.number().int().positive(),
    type: z.enum(DocumentTypes),
    source: z.enum(DocumentSources).default('APLICACION'),
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(1).max(150),
    order: z.coerce.number().int().min(0).default(0),
    includeInReport: z.boolean().default(false),
    qualityValidated: z.boolean().default(false),
    reportVersion: z.coerce.number().int().positive().optional().nullable(),
    templateVersion: optionalTrimmedString(80),
    retentionUntil: nullableDate,
});
export const DirectDocumentUploadInputSchema = DocumentMetadataInputSchema.omit({ studyId: true }).extend({
    sizeBytes: z.number().int().positive().max(1_073_741_824),
});
export const CompleteDirectDocumentUploadSchema = z.object({
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/i),
});
export const SendDocumentSchema = z.object({
    documentId: z.coerce.number().int().positive(),
    sendToPatient: z.boolean(),
    sendToAilyn: z.boolean(),
    additionalRecipients: z.array(z.string().email()).max(10).default([]),
    subjectOverride: optionalTrimmedString(300),
    bodyOverride: optionalTrimmedString(10000),
});
export const BillingHandoffInputSchema = z.object({
    status: z.enum(BillingStatuses),
    recipientEmail: optionalEmail,
    invoiceNumber: optionalTrimmedString(120),
    payerName: optionalTrimmedString(200),
    amount: z.coerce.number().nonnegative().optional().nullable(),
    pendingInformation: optionalTrimmedString(4000),
    returnComment: optionalTrimmedString(4000),
});
export const ExternalSystemHandoffInputSchema = z.object({
    systemType: z.enum(ExternalSystemTypes),
    status: z.enum(ExternalHandoffStatuses),
    externalReference: optionalTrimmedString(500),
    notes: optionalTrimmedString(4000),
    metadata: z.record(z.string(), z.unknown()).optional().nullable(),
});
export const CatalogSchema = z.object({
    name: z.string().trim().min(1).max(200),
    code: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[A-Z0-9_\-]+$/),
    active: z.boolean().default(true),
});
export const StudyTypeInputSchema = CatalogSchema.extend({
    requiresWeight: z.boolean().default(false),
    requiresHeight: z.boolean().default(false),
    minimumAttachments: z.coerce.number().int().min(0).default(0),
    maximumAttachments: z.coerce.number().int().min(0).optional().nullable(),
    requiresRecommendations: z.boolean().default(false),
    expectedDocumentTypes: z.array(z.enum(DocumentTypes)).default([]),
    reportTemplateKey: optionalTrimmedString(120),
    displayOrder: z.coerce.number().int().default(100),
});
export const StudyTypeFieldInputSchema = z.object({
    id: z.coerce.number().int().positive().optional().nullable(),
    studyTypeId: z.coerce.number().int().positive(),
    code: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[A-Z0-9_\-]+$/),
    label: z.string().trim().min(1).max(200),
    fieldType: z.enum(DynamicFieldTypes),
    required: z.boolean().default(false),
    active: z.boolean().default(true),
    displayOrder: z.coerce.number().int().default(100),
    helpText: optionalTrimmedString(1000),
    options: z
        .array(z.string().trim().min(1).max(200))
        .max(100)
        .optional()
        .nullable(),
    appliesToRole: z.enum(RoleCodes).optional().nullable(),
});
export const InstitutionInputSchema = CatalogSchema.extend({
    type: z.string().trim().min(1).max(80),
    email: optionalEmail,
    city: optionalTrimmedString(120),
});
export const ServiceLocationInputSchema = CatalogSchema.extend({
    institutionId: z.coerce.number().int().positive().optional().nullable(),
    address: optionalTrimmedString(500),
    city: optionalTrimmedString(120),
    contactName: optionalTrimmedString(200),
    contactPhone: optionalTrimmedString(40),
});
export const ThirdPartyInputSchema = z.object({
    sourceSystem: z.string().trim().min(1).max(80).default('SALUDTOOLS'),
    externalId: z.string().trim().min(1).max(120),
    name: z.string().trim().min(1).max(240),
    identificationType: z.string().trim().min(1).max(40),
    identificationNumber: z.string().trim().min(1).max(80),
    personType: z.literal('JURIDICA'),
    active: z.boolean().default(true),
});
export const AgreementInputSchema = z.object({
    sourceSystem: z.string().trim().min(1).max(80).default('SALUDTOOLS'),
    externalId: z.string().trim().min(1).max(120),
    name: z.string().trim().min(1).max(240),
    thirdPartyId: z.coerce.number().int().positive(),
    active: z.boolean().default(true),
});
export const ProfessionalInputSchema = z.object({
    fullName: z.string().trim().min(1).max(200),
    code: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[A-Z0-9_\-]+$/),
    email: z
        .string()
        .trim()
        .email()
        .transform((value) => value.toLowerCase()),
    type: z.enum(ProfessionalTypes),
    registrationNumber: optionalTrimmedString(120),
    signatureFileUrl: optionalTrimmedString(1000),
    active: z.boolean().default(true),
});
export const UserInputSchema = z.object({
    id: z.coerce.number().int().positive().optional().nullable(),
    email: z
        .string()
        .email()
        .transform((value) => value.toLowerCase()),
    displayName: z.string().trim().min(1).max(200),
    role: z.enum(RoleCodes),
    roles: z.array(z.enum(RoleCodes)).min(1).default(['RECEPCION']),
    professionalId: z.coerce.number().int().positive().optional().nullable(),
    entraTenantId: optionalTrimmedString(120),
    entraObjectId: optionalTrimmedString(120),
    active: z.boolean().default(true),
});
export const SystemParameterInputSchema = z.object({
    key: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[A-Z0-9_\-.]+$/),
    textValue: optionalTrimmedString(10000),
    numberValue: z.coerce.number().optional().nullable(),
    booleanValue: z.boolean().optional().nullable(),
    urlValue: optionalTrimmedString(1000),
    description: z.string().trim().min(1).max(4000),
    active: z.boolean().default(true),
    sensitive: z.boolean().default(false),
});
export const hasRole = (user, roles) => roles.some((role) => user.role === role || user.roles.includes(role));
export const formatStateLabel = (code) => code
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
//# sourceMappingURL=index.js.map